
// src/components/InvoicePage.js
import React, { useEffect, useState, useMemo } from "react";
import { useMediaQuery, useTheme } from '@mui/material';
import { db } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Pending as PendingIcon,
  ContentCopy as DuplicateIcon,
  Description as InvoiceIcon,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import { formatDate } from "../utils/formatHelpers";

const InvoicePage = () => {
  // Use MUI's useMediaQuery with theme for stable mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openQuickDialog, setOpenQuickDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [logoImage, setLogoImage] = useState(null);

  const [formData, setFormData] = useState({
    invoiceNumber: "",
    clientName: "",
    clientAddress: "",
    clientEmail: "",
    items: [{ description: "", quantity: 1, rate: 0 }],
    vatRate: 20,
    dueDays: 30,
    notes: "",
  });

  const [quickFormData, setQuickFormData] = useState({
    invoiceNumber: "",
    description: "",
    amount: "",
    vatRate: 20,
    dueDays: 30,
    notes: "",
  });

  // Generate next invoice number
  const generateNextInvoiceNumber = () => {
    if (invoices.length === 0) {
      return "INV-005"; // Start from 005 since current is 004
    }
    
    // Find the highest invoice number from various formats
    const invoiceNumbers = invoices
      .map(inv => inv.invoiceNumber)
      .filter(num => num)
      .map(num => {
        // Handle INV-XXX format
        const invMatch = num.match(/INV-(\d+)/i);
        if (invMatch) return parseInt(invMatch[1], 10);
        
        // Handle plain numbers like "004" or "4"
        const plainMatch = num.match(/^(\d+)$/);
        if (plainMatch) return parseInt(plainMatch[1], 10);
        
        return 0;
      });
    
    const maxNum = invoiceNumbers.length > 0 ? Math.max(...invoiceNumbers) : 4; // Default to 4 if no invoices
    const nextNum = maxNum + 1;
    return `INV-${String(nextNum).padStart(3, "0")}`;
  };

  // Load logo image when component mounts
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const logoUrl = "https://firebasestorage.googleapis.com/v0/b/humbertack-workflow.firebasestorage.app/o/Minimalist%20HumberTrack%20Logo%20Design.png?alt=media&token=7fac08dd-27a3-466a-8172-48d50a827959";
        
        const img = new Image();
        img.crossOrigin = "anonymous";
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL('image/png');
            setLogoImage(base64);
          } catch (err) {
            console.error("Error converting logo:", err);
            setLogoImage(null);
          }
        };
        
        img.onerror = () => {
          console.error("Failed to load logo image");
          setLogoImage(null);
        };
        
        img.src = logoUrl;
      } catch (error) {
        console.error("Error in loadLogo:", error);
        setLogoImage(null);
      }
    };
    
    loadLogo();
  }, []);

  // Load pending jobs (completed but not invoiced)
  useEffect(() => {
    const jobsQuery = query(
      collection(db, "jobs"),
      where("status", "==", "Completed")
    );

    const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
      const completedJobs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const invoicedJobIds = invoices.map((inv) => inv.jobId).filter(Boolean);
      const pending = completedJobs.filter(
        (job) => !invoicedJobIds.includes(job.id)
      );

      setPendingJobs(pending);
    });

    return unsubscribe;
  }, [invoices]);

  // Load invoices
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "invoices"), (snapshot) => {
      const invoiceList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInvoices(invoiceList);
    });
    return unsubscribe;
  }, []);

  // Load jobs
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "jobs"), (snapshot) => {
      const jobList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setJobs(jobList);
    });
    return unsubscribe;
  }, []);

  // Load all customers for the autocomplete dropdown
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "customers"), (snapshot) => {
      const customerList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllCustomers(customerList);
    });
    return unsubscribe;
  }, []);

  // Calculate statistics
  const statistics = useMemo(() => {
    const total = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const paid = invoices
      .filter((inv) => inv.status === "paid")
      .reduce((sum, inv) => sum + (inv.total || 0), 0);
    const unpaid = invoices
      .filter((inv) => inv.status === "unpaid")
      .reduce((sum, inv) => sum + (inv.total || 0), 0);

    const now = new Date();
    const overdue = invoices
      .filter((inv) => {
        if (inv.status === "paid") return false;
        const dueDate = inv.dueDate?.toDate ? inv.dueDate.toDate() : new Date(inv.dueDate);
        return dueDate < now;
      })
      .reduce((sum, inv) => sum + (inv.total || 0), 0);

    return {
      totalInvoiced: total,
      totalPaid: paid,
      totalDue: unpaid,
      totalOverdue: overdue,
      totalCount: invoices.length,
      paidCount: invoices.filter((inv) => inv.status === "paid").length,
      unpaidCount: invoices.filter((inv) => inv.status === "unpaid").length,
      overdueCount: invoices.filter((inv) => {
        if (inv.status === "paid") return false;
        const dueDate = inv.dueDate?.toDate ? inv.dueDate.toDate() : new Date(inv.dueDate);
        return dueDate < now;
      }).length,
    };
  }, [invoices]);

  // Filter invoices based on tab
  const filteredInvoices = useMemo(() => {
    const now = new Date();
    switch (currentTab) {
      case 0:
        return invoices;
      case 1:
        return invoices.filter((inv) => inv.status === "paid");
      case 2:
        return invoices.filter((inv) => inv.status === "unpaid");
      case 3:
        return invoices.filter((inv) => {
          if (inv.status === "paid") return false;
          const dueDate = inv.dueDate?.toDate ? inv.dueDate.toDate() : new Date(inv.dueDate);
          return dueDate < now;
        });
      default:
        return invoices;
    }
  }, [invoices, currentTab]);

  const handleOpenDialog = () => {
    setFormData({
      invoiceNumber: "",
      clientName: "",
      clientAddress: "",
      clientEmail: "",
      items: [{ description: "", quantity: 1, rate: 0 }],
      vatRate: 20,
      dueDays: 30,
      notes: "",
    });
    setOpenDialog(true);
  };

  const handleOpenQuickDialog = (job) => {
    setSelectedJob(job);
    
    // Auto-generate invoice number
    const nextInvoiceNumber = generateNextInvoiceNumber();
    
    // Auto-generate description with job type and vehicle reg
    const jobType = job.jobSheetTemplateName || job.jobTemplateName || "Service";
    const vehicleReg = job.vehicleReg || job.vehicle || "";
    const jobRef = job.jobReferenceNumber || job.id.substring(0, 8).toUpperCase();
    
    let description = `${jobType} - Job #${jobRef}`;
    if (vehicleReg) {
      description += ` - ${vehicleReg}`;
    }
    
    setQuickFormData({
      invoiceNumber: nextInvoiceNumber,
      description: description,
      amount: "",
      vatRate: 20,
      dueDays: 30,
      notes: "",
    });
    setOpenQuickDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      invoiceNumber: "",
      clientName: "",
      clientAddress: "",
      clientEmail: "",
      items: [{ description: "", quantity: 1, rate: 0 }],
      vatRate: 20,
      dueDays: 30,
      notes: "",
    });
  };

  const handleCloseQuickDialog = () => {
    setOpenQuickDialog(false);
    setSelectedJob(null);
    setQuickFormData({
      invoiceNumber: "",
      description: "",
      amount: "",
      vatRate: 20,
      dueDays: 30,
      notes: "",
    });
  };

  const handleCreateInvoice = async () => {
    try {
      const subtotal = formData.items.reduce(
        (sum, item) => sum + item.quantity * item.rate,
        0
      );
      const vat = (subtotal * formData.vatRate) / 100;
      const total = subtotal + vat;

      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + parseInt(formData.dueDays));

      const newInvoice = {
        ...formData,
        subtotal,
        vat,
        total,
        status: "unpaid",
        createdAt: Timestamp.now(),
        dueDate: Timestamp.fromDate(dueDate),
      };

      await addDoc(collection(db, "invoices"), newInvoice);
      handleCloseDialog();
      
      try {
        generatePDF(newInvoice);
      } catch (pdfError) {
        console.error("PDF generation error:", pdfError);
        alert("Invoice created but PDF generation failed. You can download it from the invoice list.");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      alert("Failed to create invoice: " + error.message);
    }
  };

  const handleCreateFromJob = async () => {
    if (!selectedJob) return;

    try {
      const amount = parseFloat(quickFormData.amount);
      
      if (isNaN(amount) || amount <= 0) {
        alert("Please enter a valid amount");
        return;
      }

      const subtotal = amount;
      const vat = (subtotal * quickFormData.vatRate) / 100;
      const total = subtotal + vat;

      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + parseInt(quickFormData.dueDays));

      // Use the description from the form (which user can edit)
      const jobDescription = quickFormData.description || `${selectedJob.jobSheetTemplateName || "Service"} - Job #${selectedJob.jobReferenceNumber || selectedJob.id.substring(0, 8).toUpperCase()}`;

      const newInvoice = {
        invoiceNumber: quickFormData.invoiceNumber,
        clientName: selectedJob.customerName,
        clientAddress: selectedJob.siteAddress || "",
        clientEmail: selectedJob.customerEmail || "",
        items: [
          {
            description: jobDescription,
            quantity: 1,
            rate: amount,
          },
        ],
        vatRate: quickFormData.vatRate,
        dueDays: quickFormData.dueDays,
        notes: quickFormData.notes,
        subtotal,
        vat,
        total,
        status: "unpaid",
        createdAt: Timestamp.now(),
        dueDate: Timestamp.fromDate(dueDate),
        jobId: selectedJob.id,
      };

      await addDoc(collection(db, "invoices"), newInvoice);
      handleCloseQuickDialog();
      
      try {
        generatePDF(newInvoice);
      } catch (pdfError) {
        console.error("PDF generation error:", pdfError);
        alert("Invoice created successfully! PDF download will be available from the invoice list.");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      alert("Failed to create invoice: " + error.message);
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        await deleteDoc(doc(db, "invoices", id));
      } catch (error) {
        console.error("Error deleting invoice:", error);
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "paid" ? "unpaid" : "paid";
      await updateDoc(doc(db, "invoices", id), { status: newStatus });
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleDuplicateInvoice = (invoice) => {
    setFormData({
      invoiceNumber: "",
      clientName: invoice.clientName,
      clientAddress: invoice.clientAddress,
      clientEmail: invoice.clientEmail,
      items: invoice.items,
      vatRate: invoice.vatRate,
      dueDays: invoice.dueDays,
      notes: invoice.notes,
    });
    setOpenDialog(true);
  };

  const generatePDF = (invoice) => {
    try {
      const doc = new jsPDF();

      if (logoImage) {
        try {
          doc.addImage(logoImage, "PNG", 20, 15, 20, 20);
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("HumberTrack Ltd", 200, 20, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.text("34 Plantation Drive", 200, 25, { align: "right" });
      doc.text("North Ferriby, HU143BE", 200, 30, { align: "right" });
      doc.text("Tel: 07897 201067", 200, 35, { align: "right" });
      doc.text("Email: installs@humbertrack.co.uk", 200, 40, { align: "right" });

      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", 20, 50);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const invoiceDate = invoice.createdAt?.toDate
        ? invoice.createdAt.toDate().toLocaleDateString()
        : new Date().toLocaleDateString();
      doc.text(`Invoice No: ${invoice.invoiceNumber || "-"}`, 20, 60);
      doc.text(`Invoice Date: ${invoiceDate}`, 20, 65);

      doc.setFont("helvetica", "bold");
      doc.text("Bill To:", 20, 75);
      doc.setFont("helvetica", "normal");
      doc.text(invoice.clientName || "", 20, 80);
      
      const addressLines = doc.splitTextToSize(invoice.clientAddress || "", 170);
      doc.text(addressLines, 20, 85);
      
      const emailY = 85 + (addressLines.length * 5);
      if (invoice.clientEmail) {
        doc.text(`Email: ${invoice.clientEmail}`, 20, emailY);
      }

      const tableStartY = emailY + 10;
      const tableData = invoice.items.map((item) => [
        item.description,
        item.quantity,
        `£${item.rate.toFixed(2)}`,
        `£${(item.quantity * item.rate).toFixed(2)}`,
      ]);

      autoTable(doc, {
        startY: tableStartY,
        head: [["Description", "Qty", "Unit", "Total"]],
        body: tableData,
        theme: "plain",
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        styles: {
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: 20, halign: "center" },
          2: { cellWidth: 30, halign: "right" },
          3: { cellWidth: 30, halign: "right" },
        },
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFont("helvetica", "normal");
      doc.text("Subtotal:", 150, finalY, { align: "right" });
      doc.text(`£${invoice.subtotal.toFixed(2)}`, 190, finalY, { align: "right" });

      doc.text(`VAT (${invoice.vatRate}%):`, 150, finalY + 5, { align: "right" });
      doc.text(`£${invoice.vat.toFixed(2)}`, 190, finalY + 5, { align: "right" });

      doc.setFont("helvetica", "bold");
      doc.text("Total:", 150, finalY + 10, { align: "right" });
      doc.text(`£${invoice.total.toFixed(2)}`, 190, finalY + 10, { align: "right" });

      doc.setFont("helvetica", "bold");
      doc.text("Payment Details:", 20, finalY + 25);
      doc.setFont("helvetica", "normal");
      doc.text("HumberTrack", 20, finalY + 30);
      doc.text("Bank: STARLING BANK", 20, finalY + 35);
      doc.text("Sort Code: 60-83-71", 20, finalY + 40);
      doc.text("Account Number: 3929 5852", 20, finalY + 45);

      if (invoice.notes) {
        doc.setFont("helvetica", "bold");
        doc.text("Notes:", 20, finalY + 55);
        doc.setFont("helvetica", "normal");
        const notesLines = doc.splitTextToSize(invoice.notes, 170);
        doc.text(notesLines, 20, finalY + 60);
      }

      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      doc.text("Thank you for your business!", 105, 280, { align: "center" });

      doc.save(`Invoice-${invoice.invoiceNumber || "draft"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF: " + error.message);
      throw error;
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, rate: 0 }],
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  return (
    <Box className="InvoicePage-root" sx={{
      p: { xs: 1, sm: 2, md: 3 },
      width: '100%',
      maxWidth: '1200px',
      mx: 'auto',
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Invoices
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          Create Invoice
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                📄 Total Invoiced
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                £{statistics.totalInvoiced.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {statistics.totalCount} invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                ✅ Paid
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                £{statistics.totalPaid.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {statistics.paidCount} invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                ⏱️ Outstanding
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                £{statistics.totalDue.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {statistics.unpaidCount} invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                ⚠️ Overdue
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                £{statistics.totalOverdue.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {statistics.overdueCount} invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Invoices Section - Simplified (no batch) */}
      {pendingJobs.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <InvoiceIcon sx={{ color: "#667eea" }} />
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              💼 Completed Jobs Ready to Invoice
            </Typography>
            <Chip
              label={`${pendingJobs.length} pending`}
              color="primary"
              size="small"
            />
          </Stack>

          <Stack spacing={1}>
            {pendingJobs.map((job) => (
              <Paper
                key={job.id}
                sx={{
                  p: 2,
                  border: "1px solid rgba(148,163,184,0.2)",
                  bgcolor: "background.paper",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#667eea",
                    transform: "translateX(4px)",
                  },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      ✓ Job #{job.jobReferenceNumber || job.id.slice(-6).toUpperCase()} - {job.customerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {job.jobSheetTemplateName || "Service"} • {job.vehicle || "N/A"}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleOpenQuickDialog(job)}
                    sx={{
                      minWidth: 120,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  >
                    Create Invoice
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: { xs: 1, sm: 2 } }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: "bold" },
          }}
        >
          <Tab label={`All (${invoices.length})`} />
          <Tab label={`Paid (${statistics.paidCount})`} />
          <Tab label={`Unpaid (${statistics.unpaidCount})`} />
          <Tab label={`Overdue (${statistics.overdueCount})`} />
        </Tabs>
      </Paper>

      {/* Invoices Table */}
      {isMobile ? (
        <Stack spacing={2} sx={{ mb: 2, width: '100%', boxSizing: 'border-box' }}>
          {filteredInvoices.length === 0 ? (
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">No invoices found</Typography>
            </Paper>
          ) : (
            filteredInvoices.map((invoice) => {
              const dueDate = invoice.dueDate?.toDate
                ? invoice.dueDate.toDate()
                : new Date(invoice.dueDate);
              const isOverdue = invoice.status === "unpaid" && dueDate < new Date();
              return (
                <Card key={invoice.id} sx={{ p: 2, width: '100%', boxSizing: 'border-box' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {invoice.invoiceNumber}
                    </Typography>
                    <Box>
                      {invoice.status === "paid" ? (
                        <Chip icon={<CheckCircleIcon />} label="Paid" size="small" color="success" />
                      ) : isOverdue ? (
                        <Chip icon={<WarningIcon />} label="Overdue" size="small" color="error" />
                      ) : (
                        <Chip icon={<PendingIcon />} label="Unpaid" size="small" color="warning" />
                      )}
                    </Box>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{invoice.clientName}</Typography>
                  <Typography variant="caption" color="text.secondary">{invoice.clientEmail}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>Total: <b>£{invoice.total.toFixed(2)}</b></Typography>
                  <Typography variant="body2">Due: {dueDate.toLocaleDateString()}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <IconButton onClick={() => handleToggleStatus(invoice.id, invoice.status)} color={invoice.status === "paid" ? "default" : "success"} title={invoice.status === "paid" ? "Mark Unpaid" : "Mark Paid"} size="small"><CheckCircleIcon /></IconButton>
                    <IconButton onClick={() => generatePDF(invoice)} color="primary" title="Download PDF" size="small"><DownloadIcon /></IconButton>
                    <IconButton onClick={() => handleDuplicateInvoice(invoice)} color="info" title="Duplicate" size="small"><DuplicateIcon /></IconButton>
                    <IconButton onClick={() => handleDeleteInvoice(invoice.id)} color="error" title="Delete" size="small"><DeleteIcon /></IconButton>
                  </Stack>
                </Card>
              );
            })
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
            <Table sx={{ width: '100%', minWidth: 0, tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, px: { xs: 1, sm: 2 } }}>Invoice #</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, px: { xs: 1, sm: 2 } }}>Client</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, px: { xs: 1, sm: 2 } }}>Total</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, px: { xs: 1, sm: 2 } }}>Status</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, px: { xs: 1, sm: 2 } }}>Due Date</TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, px: { xs: 1, sm: 2 } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No invoices found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => {
                    const dueDate = invoice.dueDate?.toDate
                      ? invoice.dueDate.toDate()
                      : new Date(invoice.dueDate);
                    const isOverdue = invoice.status === "unpaid" && dueDate < new Date();

                    return (
                      <TableRow key={invoice.id} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>{invoice.invoiceNumber}</TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                            {invoice.clientName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                            {invoice.clientEmail}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>£{invoice.total.toFixed(2)}</TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                          {invoice.status === "paid" ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Paid"
                              size="small"
                              color="success"
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' } }}
                            />
                          ) : isOverdue ? (
                            <Chip
                              icon={<WarningIcon />}
                              label="Overdue"
                              size="small"
                              color="error"
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' } }}
                            />
                          ) : (
                            <Chip
                              icon={<PendingIcon />}
                              label="Unpaid"
                              size="small"
                              color="warning"
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' } }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>{dueDate.toLocaleDateString()}</TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>
                          <IconButton
                            onClick={() => handleToggleStatus(invoice.id, invoice.status)}
                            color={invoice.status === "paid" ? "default" : "success"}
                            title={invoice.status === "paid" ? "Mark Unpaid" : "Mark Paid"}
                            size={isMobile ? "small" : "medium"}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => generatePDF(invoice)}
                            color="primary"
                            title="Download PDF"
                            size={isMobile ? "small" : "medium"}
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDuplicateInvoice(invoice)}
                            color="info"
                            title="Duplicate"
                            size={isMobile ? "small" : "medium"}
                          >
                            <DuplicateIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            color="error"
                            title="Delete"
                            size={isMobile ? "small" : "medium"}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      )}
      {/* Create Invoice Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Create New Invoice</DialogTitle>
        <DialogContent sx={{ px: { xs: 0.5, sm: 2 }, overflowX: 'hidden' }}>
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Invoice Number"
                value={formData.invoiceNumber}
                onChange={(e) =>
                  setFormData({ ...formData, invoiceNumber: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={allCustomers}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ""}
                value={formData.clientName}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    setFormData({ ...formData, clientName: newValue });
                  } else if (newValue && newValue.name) {
                    setFormData({
                      ...formData,
                      clientName: newValue.name,
                      clientAddress: newValue.address || "",
                      clientEmail: newValue.contactEmail || newValue.email || "",
                    });
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, clientName: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client Name"
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client Address"
                value={formData.clientAddress}
                onChange={(e) =>
                  setFormData({ ...formData, clientAddress: e.target.value })
                }
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Email"
                type="email"
                value={formData.clientEmail}
                onChange={(e) =>
                  setFormData({ ...formData, clientEmail: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="VAT Rate (%)"
                type="number"
                value={formData.vatRate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vatRate: parseFloat(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Due Days"
                type="number"
                value={formData.dueDays}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dueDays: parseInt(e.target.value),
                  })
                }
              />
            </Grid>

            {/* Line Items */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Line Items
              </Typography>
              {formData.items.map((item, index) => (
                <Grid container spacing={1} key={index} sx={{ mb: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={item.description}
                      onChange={(e) =>
                        updateItem(index, "description", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Qty"
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        updateItem(index, "quantity", parseInt(e.target.value))
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Rate"
                      type="number"
                      value={item.rate}
                      onChange={(e) =>
                        updateItem(index, "rate", parseFloat(e.target.value))
                      }
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 0.5 }}>£</Typography>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      onClick={() => removeItem(index)}
                      disabled={formData.items.length === 1}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Button size="small" onClick={addItem} sx={{ mt: 1 }}>
                + Add Item
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                multiline
                rows={3}
              />
            </Grid>

            {/* Preview Totals */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography>
                  Subtotal: £
                  {formData.items
                    .reduce((sum, item) => sum + item.quantity * item.rate, 0)
                    .toFixed(2)}
                </Typography>
                <Typography>
                  VAT ({formData.vatRate}%): £
                  {(
                    formData.items.reduce(
                      (sum, item) => sum + item.quantity * item.rate,
                      0
                    ) *
                    (formData.vatRate / 100)
                  ).toFixed(2)}
                </Typography>
                <Typography variant="h6">
                  Total: £
                  {(
                    formData.items.reduce(
                      (sum, item) => sum + item.quantity * item.rate,
                      0
                    ) *
                    (1 + formData.vatRate / 100)
                  ).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleCreateInvoice}
            variant="contained"
            disabled={!formData.invoiceNumber || !formData.clientName}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            Create & Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Invoice Dialog */}
      <Dialog open={openQuickDialog} onClose={handleCloseQuickDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Quick Invoice for Job #{selectedJob?.jobReferenceNumber || selectedJob?.id?.slice(-6).toUpperCase()}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 0.5, sm: 2 }, overflowX: 'hidden' }}>
          <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
            Creating invoice for: <strong>{selectedJob?.customerName}</strong>
            <br />
            Job: {selectedJob?.jobSheetTemplateName || "Service"}
          </Alert>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Invoice Number"
                value={quickFormData.invoiceNumber}
                onChange={(e) =>
                  setQuickFormData({
                    ...quickFormData,
                    invoiceNumber: e.target.value,
                  })
                }
                required
                helperText="Auto-generated, but you can edit if needed"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={quickFormData.description}
                onChange={(e) =>
                  setQuickFormData({
                    ...quickFormData,
                    description: e.target.value,
                  })
                }
                helperText="Auto-filled with job type and vehicle reg"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount (ex VAT)"
                type="number"
                value={quickFormData.amount}
                onChange={(e) =>
                  setQuickFormData({ ...quickFormData, amount: e.target.value })
                }
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="VAT Rate (%)"
                type="number"
                value={quickFormData.vatRate}
                onChange={(e) =>
                  setQuickFormData({
                    ...quickFormData,
                    vatRate: parseFloat(e.target.value),
                  })
                }
                helperText="Set to 0 for VAT exempt"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Days"
                type="number"
                value={quickFormData.dueDays}
                onChange={(e) =>
                  setQuickFormData({
                    ...quickFormData,
                    dueDays: parseInt(e.target.value),
                  })
                }
                helperText="Days until payment due"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                💳 <strong>Payment Details:</strong> Your fixed bank details (HumberTrack, Starling Bank, 60-83-71, 3929 5852) will be automatically included on the invoice.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes (Optional)"
                multiline
                rows={3}
                value={quickFormData.notes}
                onChange={(e) =>
                  setQuickFormData({ ...quickFormData, notes: e.target.value })
                }
                placeholder="Payment terms, thank you message..."
              />
            </Grid>

            {/* Preview */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  💰 Invoice Preview:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Description:</strong> {quickFormData.description || "N/A"}
                </Typography>
                <Typography variant="body1">
                  Amount: £{parseFloat(quickFormData.amount || 0).toFixed(2)}
                </Typography>
                <Typography variant="body1">
                  VAT ({quickFormData.vatRate}%):{" "}
                  £
                  {(
                    (parseFloat(quickFormData.amount || 0) *
                      quickFormData.vatRate) /
                    100
                  ).toFixed(2)}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", borderTop: "2px solid #333", pt: 1 }}
                >
                  Total:{" "}
                  £
                  {(
                    parseFloat(quickFormData.amount || 0) *
                    (1 + quickFormData.vatRate / 100)
                  ).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQuickDialog}>Cancel</Button>
          <Button
            onClick={handleCreateFromJob}
            variant="contained"
            disabled={!quickFormData.invoiceNumber || !quickFormData.amount}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            Create Invoice & Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoicePage;